import {
  PaymentForm,
  RegisterForm,
  useDataContext,
  useEventConfig,
} from "../Components";
import {
  createContext,
  ProviderProps,
  useContext,
  useEffect,
  useState,
  lazy,
  Suspense,
} from "react";

import styled from "styled-components";
import { EventTypes } from "@max/common";
import { useUserActionTrackingContext } from "./UserActionTrackingContext";
import { TrackingEventType, ClickTrackingContext } from "@max/common/analytics";
import { LightShowProvider } from "./LightShow";

const SocialFlyout = lazy(() => import("./SocialFlyout"));
const Terms = lazy(() => import("./Terms"));
const Privacy = lazy(() => import("./Privacy"));
const SweepstakesRules = lazy(() => import("./SweepstakesRules"));

export interface ActionInterface {
  handleAction: (
    arg: string,
    tracking?: { evt: TrackingEventType; ctx?: ClickTrackingContext },
  ) => Promise<void> | void;
  closing: boolean;
  showSweepstakesRules: () => void;
}

const animationDuration = 400;
const ActionContext = createContext<ActionInterface>({} as ActionInterface);

export const ActionProvider = ({
  children,
  isMock,
  ...props
}: JSX.IntrinsicAttributes &
  Omit<ProviderProps<ActionInterface>, "value"> & {
    eventId?: string;
    isMock?: boolean;
  }) => {
  const [visibleView, setVisibleView] = useState<string | null>();
  const [terms, setTerms] = useState<boolean>(false);
  const [privacy, setPrivacy] = useState<boolean>(false);
  const [sweepstakesRules, setSweepstakesRules] = useState<boolean>(false);
  const [closing, setClosing] = useState<boolean>(false);
  const { track } = useUserActionTrackingContext();
  const { type, register } = useEventConfig();
  const { data, setValue } = useDataContext();

  const handleExternalLink = (action: string) => {
    track({
      event: "click",
      context: { type: "url", value: action },
    });
    window.open(action, "_blank", "noopener");
  };

  const handleAction = (action: string) => {
    if (action.startsWith("http")) {
      handleExternalLink(action);
      return;
    }

    if (["register", "social", "payment"].includes(action)) {
      if (action === "register") {
        track({
          event: "card",
          context: { label: "Check in form" },
        });
      } else if (action === "payment") {
        track({ event: "click", context: { type: "donate", value: "start" } });
      } else if (action === "social") {
        track({
          event: "click",
          context: { type: "streaming", value: "open" },
        });
      }

      setVisibleView(action);
      return;
    }

    if (action === "close") {
      setClosing(true);
      window.setTimeout(() => {
        setVisibleView(null);
        setClosing(false);
      }, animationDuration + 10);
      return;
    }
  };

  const showTerms = () => {
    setTerms(true);
  };

  const showPrivacy = () => {
    console.log("SHOW PRIVACY!");
    setPrivacy(true);
  };

  const showSweepstakesRules = () => {
    if (register?.customSweepstakesRules?.startsWith("http")) {
      handleExternalLink(register.customSweepstakesRules);
      return;
    }
    if (!isMock) {
      track({
        event: "click",
        context: { type: "register", value: "rules" },
      });
      setSweepstakesRules(true);
    }
  };

  useEffect(() => {
    if (!isMock) {
      document.addEventListener("setfan-show-terms", showTerms);
      document.addEventListener("setfan-show-privacy", showPrivacy);
      document.addEventListener(
        "setfan-show-sweepstakesrules",
        showSweepstakesRules,
      );
      return () => {
        document.removeEventListener("setfan-show-terms", showTerms);
        document.removeEventListener("setfan-show-privacy", showPrivacy);
        document.removeEventListener(
          "setfan-show-sweepstakesrules",
          showSweepstakesRules,
        );
      };
    }
  }, []);

  const value = {
    handleAction,
    closing,
    showSweepstakesRules,
  };

  // if you have the register form open, or had it open, and you click `Post Check-in`,
  // the state is changed to invalid values for the register form and when it tries to render,
  // crashes the UI. This handles the override to close the form when the state is updated to
  // show the post check in screen.
  useEffect(() => {
    if (data?.values?.hideRegisterForm) {
      handleAction("close");
      setValue({ hideRegisterForm: false });
    }
  }, [data?.values?.hideRegisterForm]);

  return (
    <ActionContext.Provider {...props} value={value}>
      <EventRoot
        id={`event-root-${EventTypes[type].toLowerCase()}`}
        style={{
          ...(!!visibleView && {
            position: isMock ? "absolute" : "fixed",
            height: "100%",
          }),
        }}
      >
        <LightShowProvider
          disabled={isMock}
          registerOpen={visibleView === "register"}
          onCheckIn={() => handleAction("register")}
        >
          {children}
        </LightShowProvider>
        {visibleView === "register" && !data.values.hideRegisterForm && (
          <RegisterForm />
        )}
        {visibleView === "payment" && <PaymentForm />}
        <Suspense fallback={null}>
          {visibleView === "social" && <SocialFlyout />}
        </Suspense>
        <Suspense fallback={null}>
          {terms && <Terms onClose={() => setTerms(false)} />}
        </Suspense>
        <Suspense fallback={null}>
          {privacy && <Privacy onClose={() => setPrivacy(false)} />}
        </Suspense>
        <Suspense fallback={null}>
          {sweepstakesRules && (
            <SweepstakesRules onClose={() => setSweepstakesRules(false)} />
          )}
        </Suspense>
      </EventRoot>
    </ActionContext.Provider>
  );
};

export const useActionContext = () => useContext(ActionContext);

const EventRoot = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  width: 100%;
  position: relative;
`;
